import { useNavigate, useParams, useSearch } from '@tanstack/react-router';

import { Tabs } from '../../cdl/Tabs/Tabs';
import { Text } from '../../cdl/Text/Text';
import { AdvancedError } from '../../common/Error/AdvancedError';
import { translate } from '../../common/helpers/translate.helper';
import { useDocumentTitle } from '../../common/hooks/useDocumentTitle';
import { useRole } from '../../common/hooks/useRole';
import { useVesselDetails } from '../../common/hooks/useVesselDetails';
import { LoadingIndicator } from '../../common/LoadingIndicator/LoadingIndicator';
import { Page } from '../../common/Page/Page';
import { PageGrid } from '../../common/PageGrid';
import { Box } from '../../common/ui/Box';
import { VesselDetailPageContent, VesselDetailPageTabs } from '../VesselOverview/components/VesselDetailPageContent';

import { VesselActions } from './components/VesselActions';
import { useLubricationChartUpload } from './hooks/useLubricationChartUpload';

export const VesselDetailPage = () => {
    const role = useRole();

    const routePath = '/_app/vessel/$id';

    const { id } = useParams({ from: routePath });
    const search = useSearch({ from: routePath });
    const navigate = useNavigate({ from: '/vessel/$id' });

    const { isPending, isError, data: vessel } = useVesselDetails(id);
    const lubricationChartUpload = useLubricationChartUpload({ vesselId: id, customerId: vessel?.customerId });

    useDocumentTitle(
        vessel
            ? translate('vessel.documentTitle', {
                  name: vessel.name,
              })
            : undefined,
        [vessel]
    );

    const totals = {
        [VesselDetailPageTabs.OVERVIEW]: null,
        [VesselDetailPageTabs.SCHEDULE]: null,
        [VesselDetailPageTabs.ORDERS]: null,
        [VesselDetailPageTabs.PRODUCTS]: null,
        [VesselDetailPageTabs.CONTRACTS]: null,
    };

    const updateTab = (tab: VesselDetailPageTabs) => {
        navigate({ search: { tab } });
    };

    if (isPending) {
        return (
            <Page>
                <LoadingIndicator variant="page" />
            </Page>
        );
    }

    if (isError) {
        return (
            <Page>
                <AdvancedError />
            </Page>
        );
    }

    return (
        <Page>
            <PageGrid>
                <div>
                    <Box display="flex" justifyContent="space-between" gap={24}>
                        <Text as="h1" variant="headline">
                            {vessel.name}
                        </Text>

                        {role.hasWriteRights(vessel.customerId) ? <VesselActions vessel={vessel} /> : null}
                    </Box>
                    <Text variant="body" color="foreground.subtle">
                        {vessel.imo}
                    </Text>
                </div>

                <Tabs selectedContext={search.tab} onSelect={updateTab} totals={totals} />

                <VesselDetailPageContent vessel={vessel} lubricationChartUpload={lubricationChartUpload} />
            </PageGrid>
        </Page>
    );
};
